import { useState, useEffect } from "react";
import { AdminLayout } from "@/components/shared/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  getDealerApplications, updateApplicationStatus, adminCreateApplication,
  bulkUploadApplications, updateApplication, getApplicationDocuments,
  uploadApplicationDocument, deleteApplicationDocument,
  getApplicationCredentials, setApplicationCredentials,
  updateApplicationSubAgentId, convertApplicationToDealer,
} from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  UserPlus, Filter, Eye, CheckCircle, XCircle, MapPin, Building,
  DollarSign, Clock, Loader2, Plus, Upload, Edit, FileText, Trash2, Download, Key,
  ArrowRight, ShieldCheck, Search, UserCheck, AlertCircle, Hash,
} from "lucide-react";
import { exportToCSV } from "@/lib/exportCSV";

const STATUS_CONFIG = {
  pending: { label: "Pending", color: "bg-yellow-100 text-yellow-700", description: "Dealer must upload required documents" },
  vetting: { label: "Vetting", color: "bg-blue-100 text-blue-700", description: "Documents sent to Total Wireless for review" },
  pre_approved: { label: "Pre-Approved", color: "bg-purple-100 text-purple-700", description: "Awaiting additional info & sub-agent ID" },
  approved: { label: "Approved", color: "bg-green-100 text-green-700", description: "Ready to convert to dealer" },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-700", description: "Application rejected" },
};

const downloadSampleCSV = (type) => {
  const samples = {
    applications: { filename: "sample_applications.csv", content: "first_name,last_name,email,phone,company_name,business_address,city,state,zip_code,years_experience,current_locations,investment_capital,message\nJohn,Doe,john@example.com,555-0100,Acme Wireless,123 Main St,Miami,FL,33101,5,2,50000-100000,Looking to expand" },
  };
  const { filename, content } = samples[type];
  const blob = new Blob([content], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const emptyApp = {
  first_name: "", last_name: "", email: "", phone: "", company_name: "",
  business_address: "", city: "", state: "", zip_code: "",
  years_experience: 0, current_locations: 0, investment_capital: "", message: "",
  location_name: "", tsp_id: "", tw_dc: "", store_phone: "", store_email: "",
};

/**
 * Hoisted so the DOM tree (including the Sub-Agent ID <Input>) survives
 * parent re-renders — otherwise React unmounts the whole subtree between
 * keystrokes and the input loses focus every character.
 */
function StatusActions({
  app, processing,
  subAgentId, setSubAgentId, savingSubAgent,
  handleStatusChange, handleSaveSubAgentId, handleConvertToDealer,
}) {
  const s = app.status;
  const cfg = STATUS_CONFIG[s];
  return (
    <div className="space-y-3 pt-4 border-t border-slate-200">
      <div className={`p-3 rounded-lg ${s === "rejected" ? "bg-red-50" : s === "approved" ? "bg-green-50" : "bg-slate-50"}`}>
        <div className="flex items-center gap-2 mb-1">
          <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${cfg?.color}`}>{cfg?.label}</span>
          {app.sub_agent_id && <span className="text-xs text-slate-500 flex items-center gap-1"><Hash className="w-3 h-3" />Sub-Agent: {app.sub_agent_id}</span>}
        </div>
        <p className="text-xs text-slate-500">{cfg?.description}</p>
      </div>
      {!["rejected"].includes(s) && (
        <div className="flex items-center gap-1 text-xs">
          {["pending", "vetting", "pre_approved", "approved"].map((step, i) => {
            const steps = ["pending", "vetting", "pre_approved", "approved"];
            const currentIdx = steps.indexOf(s);
            const isActive = i === currentIdx;
            const isDone = i < currentIdx;
            return (
              <div key={step} className="flex items-center gap-1">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold
                  ${isDone ? "bg-teal text-white" : isActive ? "bg-navy text-white" : "bg-slate-200 text-slate-400"}`}>
                  {isDone ? <CheckCircle className="w-3.5 h-3.5" /> : i + 1}
                </div>
                <span className={`hidden sm:inline ${isActive ? "font-semibold text-navy" : isDone ? "text-teal" : "text-slate-400"}`}>
                  {STATUS_CONFIG[step]?.label}
                </span>
                {i < 3 && <ArrowRight className="w-3 h-3 text-slate-300 mx-1" />}
              </div>
            );
          })}
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {s === "pending" && (
          <>
            <Button onClick={() => handleStatusChange(app.id, "vetting")} disabled={processing} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="status-vetting-btn">
              <Search className="w-4 h-4 mr-1" />Send to Vetting
            </Button>
            <Button onClick={() => handleStatusChange(app.id, "rejected")} disabled={processing} variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" data-testid="reject-application-btn">
              <XCircle className="w-4 h-4 mr-1" />Reject
            </Button>
          </>
        )}
        {s === "vetting" && (
          <>
            <Button onClick={() => handleStatusChange(app.id, "pre_approved")} disabled={processing} size="sm" className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="status-pre-approved-btn">
              <ShieldCheck className="w-4 h-4 mr-1" />Pre-Approve
            </Button>
            <Button onClick={() => handleStatusChange(app.id, "pending")} disabled={processing} variant="outline" size="sm" data-testid="status-back-pending-btn">
              <ArrowRight className="w-4 h-4 mr-1 rotate-180" />Back to Pending
            </Button>
            <Button onClick={() => handleStatusChange(app.id, "rejected")} disabled={processing} variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50">
              <XCircle className="w-4 h-4 mr-1" />Reject
            </Button>
          </>
        )}
        {s === "pre_approved" && (
          <>
            <div className="w-full flex items-end gap-2 mb-1">
              <div className="flex-1">
                <Label className="text-xs text-slate-500">Sub-Agent ID</Label>
                <Input value={subAgentId} onChange={(e) => setSubAgentId(e.target.value)}
                  placeholder="Enter sub-agent ID" className="mt-1" data-testid="sub-agent-id-input" />
              </div>
              <Button onClick={handleSaveSubAgentId} disabled={savingSubAgent} size="sm" variant="outline" data-testid="save-sub-agent-btn">
                {savingSubAgent ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
              </Button>
            </div>
            <Button onClick={() => handleStatusChange(app.id, "approved")} disabled={processing} size="sm" className="bg-green-600 hover:bg-green-700 text-white" data-testid="status-approved-btn">
              <CheckCircle className="w-4 h-4 mr-1" />Approve
            </Button>
            <Button onClick={() => handleStatusChange(app.id, "vetting")} disabled={processing} variant="outline" size="sm" data-testid="status-back-vetting-btn">
              <ArrowRight className="w-4 h-4 mr-1 rotate-180" />Back to Vetting
            </Button>
            <Button onClick={() => handleStatusChange(app.id, "rejected")} disabled={processing} variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50">
              <XCircle className="w-4 h-4 mr-1" />Reject
            </Button>
          </>
        )}
        {s === "approved" && (
          <>
            <Button onClick={handleConvertToDealer} disabled={processing} size="sm" className="bg-teal hover:bg-teal-dark text-white" data-testid="convert-to-dealer-btn">
              {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserCheck className="w-4 h-4 mr-1" />}
              Convert to Dealer
            </Button>
            <Button onClick={() => handleStatusChange(app.id, "pre_approved")} disabled={processing} variant="outline" size="sm" data-testid="status-back-pre-approved-btn">
              <ArrowRight className="w-4 h-4 mr-1 rotate-180" />Back to Pre-Approved
            </Button>
          </>
        )}
        {s === "rejected" && (
          <Button onClick={() => handleStatusChange(app.id, "pending")} disabled={processing} variant="outline" size="sm" data-testid="reopen-application-btn">
            <ArrowRight className="w-4 h-4 mr-1 rotate-180" />Re-open as Pending
          </Button>
        )}
      </div>
    </div>
  );
}

export default function AdminApplications() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedApp, setSelectedApp] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState(emptyApp);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [detailTab, setDetailTab] = useState("details");
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [credentials, setCredentials] = useState({ email: "", password: "", has_password: false });
  const [credSaving, setCredSaving] = useState(false);
  const [subAgentId, setSubAgentId] = useState("");
  const [savingSubAgent, setSavingSubAgent] = useState(false);

  const fetchApplications = async () => {
    try {
      const data = await getDealerApplications(statusFilter === "all" ? null : statusFilter);
      setApplications(data);
    } catch (error) { console.error("Error:", error); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchApplications(); }, [statusFilter]);

  const filteredApplications = applications.filter((app) =>
    app.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    app.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    app.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (app.company_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (app.city || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (app.state || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleOpenDetail = async (app) => {
    setSelectedApp(app);
    setEditMode(false);
    setDetailTab("details");
    setSubAgentId(app.sub_agent_id || "");
    setEditData({
      first_name: app.first_name, last_name: app.last_name, email: app.email,
      phone: app.phone, company_name: app.company_name,
      business_address: app.business_address, city: app.city, state: app.state,
      zip_code: app.zip_code, years_experience: app.years_experience,
      current_locations: app.current_locations, investment_capital: app.investment_capital,
      message: app.message,
      location_name: app.location_name || "", tsp_id: app.tsp_id || "", tw_dc: app.tw_dc || "",
      store_phone: app.store_phone || "", store_email: app.store_email || "",
    });
    try {
      const docs = await getApplicationDocuments(app.id);
      setDocuments(docs);
    } catch { setDocuments([]); }
    try {
      const creds = await getApplicationCredentials(app.id);
      setCredentials({ email: creds.email || app.email, password: "", has_password: creds.has_password || false });
    } catch { setCredentials({ email: app.email, password: "", has_password: false }); }
  };

  const handleSaveEdit = async () => {
    setProcessing(true);
    try {
      await updateApplication(selectedApp.id, editData);
      toast.success("Application updated");
      setEditMode(false);
      setSelectedApp({ ...selectedApp, ...editData });
      fetchApplications();
    } catch { toast.error("Failed to update"); }
    setProcessing(false);
  };

  const handleStatusChange = async (appId, newStatus) => {
    setProcessing(true);
    try {
      await updateApplicationStatus(appId, newStatus);
      toast.success(`Status updated to ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
      setSelectedApp((prev) => prev ? { ...prev, status: newStatus } : null);
      fetchApplications();
    } catch { toast.error("Failed to update status"); }
    setProcessing(false);
  };

  const handleSaveSubAgentId = async () => {
    setSavingSubAgent(true);
    try {
      await updateApplicationSubAgentId(selectedApp.id, subAgentId);
      toast.success("Sub-Agent ID saved");
      setSelectedApp({ ...selectedApp, sub_agent_id: subAgentId });
      fetchApplications();
    } catch { toast.error("Failed to save"); }
    setSavingSubAgent(false);
  };

  const handleConvertToDealer = async () => {
    if (!window.confirm("Convert this applicant to a full dealer? This will create a dealer account, copy all documents, and create a draft Location from the application's business address.")) return;
    setProcessing(true);
    try {
      const result = await convertApplicationToDealer(selectedApp.id);
      toast.success(result.message);
      if (result.location_created) {
        toast.success(`Location created: ${result.location_slug}`, { duration: 6000 });
      }
      setSelectedApp(null);
      fetchApplications();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to convert"); }
    setProcessing(false);
  };

  const handleUploadDoc = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const doc = await uploadApplicationDocument(selectedApp.id, file);
      setDocuments([doc, ...documents]);
      toast.success("Document uploaded");
    } catch { toast.error("Failed to upload"); }
    setUploading(false);
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await deleteApplicationDocument(selectedApp.id, docId);
      setDocuments(documents.filter((d) => d.id !== docId));
      toast.success("Document deleted");
    } catch { toast.error("Failed to delete"); }
  };

  const handleCreateApp = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await adminCreateApplication(formData);
      toast.success("Application created");
      setIsCreateOpen(false);
      setFormData(emptyApp);
      fetchApplications();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create"); }
    setSubmitting(false);
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSubmitting(true);
    try {
      const result = await bulkUploadApplications(file);
      toast.success(`Created ${result.created} applications`);
      if (result.errors.length > 0) toast.error(`${result.errors.length} errors`);
      setIsBulkOpen(false);
      fetchApplications();
    } catch { toast.error("Bulk upload failed"); }
    setSubmitting(false);
  };

  const handleSaveCredentials = async () => {
    if (!credentials.email || !credentials.password) return toast.error("Email and password required");
    setCredSaving(true);
    try {
      await setApplicationCredentials(selectedApp.id, { email: credentials.email, password: credentials.password });
      toast.success("Credentials saved & account created");
      setCredentials({ ...credentials, has_password: true, password: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    setCredSaving(false);
  };

  // Status action buttons based on current status. NOTE: defined outside the
  // parent component below so React keeps the input's DOM node between
  // renders — otherwise every keystroke unmounts the tree and steals focus
  // (see StatusActions definition near top of file).
  const statusActions = (
    <StatusActions
      app={selectedApp}
      processing={processing}
      subAgentId={subAgentId}
      setSubAgentId={setSubAgentId}
      savingSubAgent={savingSubAgent}
      handleStatusChange={handleStatusChange}
      handleSaveSubAgentId={handleSaveSubAgentId}
      handleConvertToDealer={handleConvertToDealer}
    />
  );

  return (
    <AdminLayout title="Dealer Applications" breadcrumb="Applications">
      <div className="space-y-6" data-testid="admin-applications-page">
        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          <div className="flex gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search applications..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9 w-64" data-testid="application-search-input" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-44" data-testid="application-status-filter">
                <Filter className="w-4 h-4 mr-2" /><SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="vetting">Vetting</SelectItem>
                <SelectItem value="pre_approved">Pre-Approved</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => exportToCSV(filteredApplications, [
              { label: "First Name", accessor: "first_name" },
              { label: "Last Name", accessor: "last_name" },
              { label: "Email", accessor: "email" },
              { label: "Phone", accessor: "phone" },
              { label: "Company", accessor: "company_name" },
              { label: "City", accessor: "city" },
              { label: "State", accessor: "state" },
              { label: "Status", accessor: "status" },
              { label: "Date", accessor: (r) => r.created_at ? new Date(r.created_at).toLocaleDateString() : "" },
            ], "applications")} data-testid="export-applications-btn">
              <Download className="w-4 h-4 mr-2" />Export
            </Button>
            <Button variant="outline" onClick={() => setIsBulkOpen(true)} data-testid="bulk-upload-apps-btn">
              <Upload className="w-4 h-4 mr-2" />Bulk Upload
            </Button>
            <Button onClick={() => setIsCreateOpen(true)} className="bg-teal hover:bg-teal-dark text-white" data-testid="create-application-btn">
              <Plus className="w-4 h-4 mr-2" />New Application
            </Button>
          </div>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-navy flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-yellow-500" />Dealer Applications
                </CardTitle>
                <span className="text-sm text-slate-500 font-body">{filteredApplications.length} applications</span>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-slate-500 text-center py-8 font-body">Loading...</p>
              ) : filteredApplications.length === 0 ? (
                <div className="text-center py-8"><UserPlus className="w-12 h-12 text-slate-300 mx-auto mb-4" /><p className="text-slate-500 font-body">No applications found</p></div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="applications-table">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">Applicant</th>
                        <th className="text-left py-3 px-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">Company</th>
                        <th className="text-left py-3 px-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">Location</th>
                        <th className="text-left py-3 px-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">Date</th>
                        <th className="text-left py-3 px-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">Status</th>
                        <th className="text-right py-3 px-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredApplications.map((app) => (
                        <tr key={app.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                          <td className="py-3 px-2">
                            <p className="font-medium text-navy font-body">{app.first_name} {app.last_name}</p>
                            <p className="text-xs text-slate-500 font-body">{app.email}</p>
                          </td>
                          <td className="py-3 px-2 text-sm text-slate-600 font-body">{app.company_name}</td>
                          <td className="py-3 px-2 text-sm text-slate-600 font-body">{app.city}{app.city && app.state ? ", " : ""}{app.state}</td>
                          <td className="py-3 px-2 text-sm text-slate-600 font-body">{new Date(app.created_at).toLocaleDateString()}</td>
                          <td className="py-3 px-2">
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${STATUS_CONFIG[app.status]?.color || "bg-slate-100 text-slate-700"}`}>
                              {STATUS_CONFIG[app.status]?.label || app.status}
                            </span>
                            {app.sub_agent_id && <span className="block text-[10px] text-slate-400 mt-0.5">SA: {app.sub_agent_id}</span>}
                          </td>
                          <td className="py-3 px-2 text-right">
                            <Button variant="ghost" size="sm" onClick={() => handleOpenDetail(app)} data-testid={`view-application-${app.id}`}>
                              <Eye className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Application Detail Dialog */}
        <Dialog open={!!selectedApp} onOpenChange={() => setSelectedApp(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle className="font-heading text-navy">Application Details</DialogTitle>
                {selectedApp && !editMode && (
                  <Button variant="outline" size="sm" onClick={() => { setEditMode(true); setDetailTab("details"); }} data-testid="edit-application-btn">
                    <Edit className="w-4 h-4 mr-1" />Edit
                  </Button>
                )}
              </div>
            </DialogHeader>
            {selectedApp && (
              <Tabs value={detailTab} onValueChange={setDetailTab}>
                <TabsList>
                  <TabsTrigger value="details">Details</TabsTrigger>
                  <TabsTrigger value="documents">Documents</TabsTrigger>
                  <TabsTrigger value="credentials">Credentials</TabsTrigger>
                </TabsList>

                <TabsContent value="details" className="mt-4">
                  <div className="space-y-6">
                    {editMode ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div><Label>First Name</Label><Input value={editData.first_name} onChange={(e) => setEditData({ ...editData, first_name: e.target.value })} data-testid="edit-app-first-name" /></div>
                          <div><Label>Last Name</Label><Input value={editData.last_name} onChange={(e) => setEditData({ ...editData, last_name: e.target.value })} data-testid="edit-app-last-name" /></div>
                          <div><Label>Email</Label><Input value={editData.email} onChange={(e) => setEditData({ ...editData, email: e.target.value })} data-testid="edit-app-email" /></div>
                          <div><Label>Phone</Label><Input value={editData.phone} onChange={(e) => setEditData({ ...editData, phone: e.target.value })} data-testid="edit-app-phone" /></div>
                          <div className="col-span-2"><Label>Company</Label><Input value={editData.company_name} onChange={(e) => setEditData({ ...editData, company_name: e.target.value })} data-testid="edit-app-company" /></div>
                          <div className="col-span-2"><Label>Business Address</Label><Input value={editData.business_address} onChange={(e) => setEditData({ ...editData, business_address: e.target.value })} /></div>
                          <div><Label>City</Label><Input value={editData.city} onChange={(e) => setEditData({ ...editData, city: e.target.value })} /></div>
                          <div><Label>State</Label><Input value={editData.state} onChange={(e) => setEditData({ ...editData, state: e.target.value })} /></div>
                          <div><Label>Zip Code</Label><Input value={editData.zip_code} onChange={(e) => setEditData({ ...editData, zip_code: e.target.value })} /></div>
                          <div><Label>Years Experience</Label><Input type="number" value={editData.years_experience} onChange={(e) => setEditData({ ...editData, years_experience: parseInt(e.target.value) || 0 })} /></div>
                          <div><Label>Current Locations</Label><Input type="number" value={editData.current_locations} onChange={(e) => setEditData({ ...editData, current_locations: parseInt(e.target.value) || 0 })} /></div>
                          <div><Label>Investment Capital</Label><Input value={editData.investment_capital} onChange={(e) => setEditData({ ...editData, investment_capital: e.target.value })} /></div>
                        </div>
                        {/* Location + Carrier IDs — flow through to the auto-created Location on convert-to-dealer */}
                        <div className="rounded-lg border border-teal/30 bg-teal/5 p-3 space-y-3" data-testid="applicant-location-section">
                          <p className="text-xs font-semibold text-teal uppercase tracking-wide">Location &amp; Carrier IDs</p>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2"><Label>Location Name</Label><Input value={editData.location_name || ""} onChange={(e) => setEditData({ ...editData, location_name: e.target.value })} placeholder="Total Wireless — 4th Ave" data-testid="edit-app-location-name" /></div>
                            <div><Label>TSP ID</Label><Input value={editData.tsp_id || ""} onChange={(e) => setEditData({ ...editData, tsp_id: e.target.value })} placeholder="Total Sales Point ID" data-testid="edit-app-tsp-id" /></div>
                            <div><Label>TW DC</Label><Input value={editData.tw_dc || ""} onChange={(e) => setEditData({ ...editData, tw_dc: e.target.value })} placeholder="Total Wireless DC" data-testid="edit-app-tw-dc" /></div>
                            <div><Label>Store Phone (optional)</Label><Input value={editData.store_phone || ""} onChange={(e) => setEditData({ ...editData, store_phone: e.target.value })} placeholder="Leave blank for Coming soon" data-testid="edit-app-store-phone" /></div>
                            <div><Label>Store Email (optional)</Label><Input type="email" value={editData.store_email || ""} onChange={(e) => setEditData({ ...editData, store_email: e.target.value })} placeholder="Leave blank for Coming soon" data-testid="edit-app-store-email" /></div>
                          </div>
                          <p className="text-[11px] text-slate-500">These become the location name, TSP ID and TW DC on the new Location that gets created when this applicant is converted to a dealer.</p>
                        </div>
                        <div><Label>Message</Label><Textarea value={editData.message || ""} onChange={(e) => setEditData({ ...editData, message: e.target.value })} rows={3} /></div>
                        <div className="flex gap-2">
                          <Button onClick={handleSaveEdit} disabled={processing} className="bg-teal hover:bg-teal-dark text-white flex-1" data-testid="save-app-edit-btn">
                            {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Save Changes
                          </Button>
                          <Button variant="outline" onClick={() => setEditMode(false)}>Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-2 gap-4">
                          <div><Label className="text-slate-500">Full Name</Label><p className="font-medium text-navy">{selectedApp.first_name} {selectedApp.last_name}</p></div>
                          <div><Label className="text-slate-500">Email</Label><p className="font-medium text-navy">{selectedApp.email}</p></div>
                          <div><Label className="text-slate-500">Phone</Label><p className="font-medium text-navy">{selectedApp.phone}</p></div>
                          <div><Label className="text-slate-500">Company</Label><p className="font-medium text-navy">{selectedApp.company_name}</p></div>
                        </div>
                        {selectedApp.business_address && (
                          <div className="p-4 bg-slate-50 rounded-lg">
                            <div className="flex items-center gap-2 mb-2"><MapPin className="w-4 h-4 text-slate-400" /><Label className="text-slate-500">Business Location</Label></div>
                            <p className="font-medium text-navy">{selectedApp.business_address}{selectedApp.business_address && <br />}{selectedApp.city}{selectedApp.city && ", "}{selectedApp.state} {selectedApp.zip_code}</p>
                          </div>
                        )}
                        <div className="grid grid-cols-3 gap-4">
                          <div className="p-4 bg-slate-50 rounded-lg text-center"><Clock className="w-5 h-5 text-teal mx-auto mb-2" /><p className="text-2xl font-heading font-bold text-navy">{selectedApp.years_experience}</p><p className="text-xs text-slate-500 font-body">Years Experience</p></div>
                          <div className="p-4 bg-slate-50 rounded-lg text-center"><Building className="w-5 h-5 text-teal mx-auto mb-2" /><p className="text-2xl font-heading font-bold text-navy">{selectedApp.current_locations}</p><p className="text-xs text-slate-500 font-body">Current Locations</p></div>
                          <div className="p-4 bg-slate-50 rounded-lg text-center"><DollarSign className="w-5 h-5 text-teal mx-auto mb-2" /><p className="text-lg font-heading font-bold text-navy">{selectedApp.investment_capital || "N/A"}</p><p className="text-xs text-slate-500 font-body">Investment Capital</p></div>
                        </div>
                        {true && (
                          <div className="p-4 bg-teal/5 rounded-lg border border-teal/20" data-testid="app-detail-location-block">
                            <Label className="text-teal font-semibold text-xs uppercase tracking-wide">Location &amp; Carrier IDs</Label>
                            <div className="grid grid-cols-3 gap-3 mt-2 text-sm">
                              <div><p className="text-[11px] text-slate-500">Location Name</p><p className="font-medium text-navy">{selectedApp.location_name || "—"}</p></div>
                              <div><p className="text-[11px] text-slate-500">TSP ID</p><p className="font-medium text-navy">{selectedApp.tsp_id || "—"}</p></div>
                              <div><p className="text-[11px] text-slate-500">TW DC</p><p className="font-medium text-navy">{selectedApp.tw_dc || "—"}</p></div>
                              <div><p className="text-[11px] text-slate-500">Store Phone</p><p className="font-medium text-navy" data-testid="app-detail-store-phone">{selectedApp.store_phone || "Coming soon"}</p></div>
                              <div className="col-span-2"><p className="text-[11px] text-slate-500">Store Email</p><p className="font-medium text-navy break-all" data-testid="app-detail-store-email">{selectedApp.store_email || "Coming soon"}</p></div>
                            </div>
                          </div>
                        )}
                        {selectedApp.extra_answers && Object.keys(selectedApp.extra_answers).length > 0 && (
                          <div className="p-4 bg-slate-50 rounded-lg" data-testid="app-detail-extra-answers">
                            <Label className="text-slate-500 text-xs uppercase tracking-wide">Other answers</Label>
                            {Object.entries(selectedApp.extra_answers).map(([q, a]) => (
                              <div key={q} className="mt-2 text-sm"><p className="text-[11px] text-slate-500">{q}</p><p className="font-medium text-navy whitespace-pre-wrap">{a}</p></div>
                            ))}
                          </div>
                        )}
                        {selectedApp.message && (
                          <div><Label className="text-slate-500">Message</Label><p className="text-slate-700 text-sm mt-1 p-3 bg-slate-50 rounded-lg font-body">{selectedApp.message}</p></div>
                        )}
                        {/* Related applications — same email = same applicant */}
                        {(() => {
                          const related = applications.filter(
                            (a) => a.id !== selectedApp.id && (a.email || "").toLowerCase() === (selectedApp.email || "").toLowerCase()
                          );
                          if (related.length === 0) return null;
                          return (
                            <div data-testid="admin-app-related">
                              <Label className="text-slate-500">Other Applications from {selectedApp.email}</Label>
                              <div className="mt-1 p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-2 font-body">
                                <p className="text-xs text-amber-900">
                                  This applicant has submitted <strong>{related.length + 1} applications</strong> total.
                                  Approving + converting any one will auto-link to the same dealer account.
                                </p>
                                <ul className="text-xs space-y-1">
                                  {related.map((r) => (
                                    <li key={r.id} className="flex items-center justify-between gap-2 bg-white px-2 py-1.5 rounded border border-amber-100">
                                      <button
                                        type="button"
                                        className="text-left flex-1 text-navy hover:text-teal truncate"
                                        onClick={() => setSelectedApp(r)}
                                        data-testid={`related-app-${r.id}`}
                                      >
                                        {r.company_name || `${r.first_name} ${r.last_name}`} — {r.business_address}, {r.city}, {r.state}
                                      </button>
                                      <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full whitespace-nowrap ${
                                        r.status === "converted_to_dealer" ? "bg-green-100 text-green-700"
                                        : r.status === "approved" ? "bg-blue-100 text-blue-700"
                                        : r.status === "rejected" ? "bg-red-100 text-red-700"
                                        : "bg-slate-100 text-slate-600"
                                      }`}>{r.status}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          );
                        })()}
                        {selectedApp.submit_source && (
                          <div data-testid="admin-app-submit-source">
                            <Label className="text-slate-500">Conversion Source</Label>
                            <div className="mt-1 p-3 bg-slate-50 rounded-lg text-xs font-body space-y-1.5 text-slate-700">
                              {selectedApp.submit_source.first_touch?.pathname && (
                                <div><span className="font-semibold text-navy">First landed on:</span> {selectedApp.submit_source.first_touch.pathname}{selectedApp.submit_source.first_touch.hash || ""}</div>
                              )}
                              {selectedApp.submit_source.last_touch?.pathname && (
                                <div><span className="font-semibold text-navy">Submitted from:</span> {selectedApp.submit_source.last_touch.pathname}{selectedApp.submit_source.last_touch.hash || ""}</div>
                              )}
                              {selectedApp.submit_source.first_touch?.referrer && (
                                <div className="break-all"><span className="font-semibold text-navy">External referrer:</span> {selectedApp.submit_source.first_touch.referrer}</div>
                              )}
                              {selectedApp.submit_source.utm && Object.keys(selectedApp.submit_source.utm).length > 0 && (
                                <div><span className="font-semibold text-navy">UTM:</span> {Object.entries(selectedApp.submit_source.utm).map(([k,v]) => `${k}=${v}`).join(" · ")}</div>
                              )}
                            </div>
                          </div>
                        )}
                        {statusActions}
                      </>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="documents" className="mt-4">
                  <div className="space-y-4">
                    <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center">
                      <input type="file" onChange={handleUploadDoc} className="hidden" id="app-doc-upload" data-testid="app-doc-upload" />
                      <label htmlFor="app-doc-upload" className="cursor-pointer">
                        {uploading ? <Loader2 className="w-6 h-6 animate-spin text-teal mx-auto" /> : (
                          <><Upload className="w-6 h-6 text-slate-400 mx-auto mb-1" /><p className="text-sm text-slate-600 font-body">Click to upload document</p></>
                        )}
                      </label>
                    </div>
                    {documents.length === 0 ? (
                      <p className="text-sm text-slate-400 font-body text-center py-4">No documents uploaded yet</p>
                    ) : (
                      <div className="space-y-2">
                        {documents.map((doc) => (
                          <div key={doc.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`app-doc-${doc.id}`}>
                            <div className="flex items-center gap-3">
                              <FileText className="w-5 h-5 text-slate-400" />
                              <div>
                                <p className="text-sm font-medium text-navy font-body">{doc.original_filename}</p>
                                <div className="flex items-center gap-2 text-xs text-slate-400">
                                  {doc.doc_type && doc.doc_type !== "general" && <span className="bg-slate-200 px-1.5 py-0.5 rounded text-slate-600">{doc.doc_type.replace(/_/g, " ")}</span>}
                                  <span>{doc.uploaded_by} - {new Date(doc.created_at).toLocaleDateString()}</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <a href={`${process.env.REACT_APP_BACKEND_URL}${doc.url}`} target="_blank" rel="noopener noreferrer">
                                <Button variant="ghost" size="sm"><Download className="w-4 h-4" /></Button>
                              </a>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteDoc(doc.id)} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="credentials" className="mt-4">
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Key className="w-5 h-5 text-teal" />
                      <h3 className="font-medium text-navy">Applicant Login Credentials</h3>
                    </div>
                    {credentials.has_password && (
                      <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                        <p className="text-sm text-emerald-700 font-medium">Account created. Applicant can log in to upload documents.</p>
                      </div>
                    )}
                    <div>
                      <Label>Login Email</Label>
                      <Input value={credentials.email} onChange={(e) => setCredentials({ ...credentials, email: e.target.value })} data-testid="cred-email-input" />
                    </div>
                    <div>
                      <Label>{credentials.has_password ? "New Password (leave blank to keep current)" : "Password *"}</Label>
                      <Input type="password" value={credentials.password} onChange={(e) => setCredentials({ ...credentials, password: e.target.value })} placeholder={credentials.has_password ? "Leave blank to keep current" : "Set password"} data-testid="cred-password-input" />
                    </div>
                    <Button onClick={handleSaveCredentials} disabled={credSaving} className="w-full bg-teal hover:bg-teal/90" data-testid="save-credentials-btn">
                      {credSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Key className="w-4 h-4 mr-2" />}
                      {credentials.has_password ? "Update Credentials" : "Create Account & Credentials"}
                    </Button>
                    <p className="text-xs text-slate-500">
                      Create credentials so the applicant can log in to the dealer portal to upload their required documents (State ID, EIN, Articles of Incorporation, Financials, Org Chart, Business Plan).
                    </p>
                  </div>
                </TabsContent>
              </Tabs>
            )}
          </DialogContent>
        </Dialog>

        {/* Create Application Dialog */}
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading text-navy">Create New Application</DialogTitle></DialogHeader>
            <form onSubmit={handleCreateApp} className="space-y-4" data-testid="create-application-form">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>First Name *</Label><Input value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} required data-testid="new-app-first-name" /></div>
                <div><Label>Last Name *</Label><Input value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} required data-testid="new-app-last-name" /></div>
                <div><Label>Email *</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required data-testid="new-app-email" /></div>
                <div><Label>Phone *</Label><Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required data-testid="new-app-phone" /></div>
                <div className="col-span-2"><Label>Company Name *</Label><Input value={formData.company_name} onChange={(e) => setFormData({ ...formData, company_name: e.target.value })} required data-testid="new-app-company" /></div>
                <div className="col-span-2"><Label>Business Address</Label><Input value={formData.business_address} onChange={(e) => setFormData({ ...formData, business_address: e.target.value })} /></div>
                <div><Label>City</Label><Input value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} /></div>
                <div><Label>State</Label><Input value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })} /></div>
                <div><Label>Zip Code</Label><Input value={formData.zip_code} onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })} /></div>
                <div><Label>Years Experience</Label><Input type="number" value={formData.years_experience} onChange={(e) => setFormData({ ...formData, years_experience: parseInt(e.target.value) || 0 })} /></div>
                <div><Label>Current Locations</Label><Input type="number" value={formData.current_locations} onChange={(e) => setFormData({ ...formData, current_locations: parseInt(e.target.value) || 0 })} /></div>
                <div><Label>Investment Capital</Label><Input value={formData.investment_capital} onChange={(e) => setFormData({ ...formData, investment_capital: e.target.value })} placeholder="$50,000 - $100,000" /></div>
              </div>
              {/* Location + Carrier IDs — feed the new Location when converted to dealer */}
              <div className="rounded-lg border border-teal/30 bg-teal/5 p-3 space-y-3" data-testid="new-app-location-section">
                <p className="text-xs font-semibold text-teal uppercase tracking-wide">Location &amp; Carrier IDs</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2"><Label>Location Name</Label><Input value={formData.location_name} onChange={(e) => setFormData({ ...formData, location_name: e.target.value })} placeholder="Total Wireless — 4th Ave" data-testid="new-app-location-name" /></div>
                  <div><Label>TSP ID</Label><Input value={formData.tsp_id} onChange={(e) => setFormData({ ...formData, tsp_id: e.target.value })} placeholder="Total Sales Point ID" data-testid="new-app-tsp-id" /></div>
                  <div><Label>TW DC</Label><Input value={formData.tw_dc} onChange={(e) => setFormData({ ...formData, tw_dc: e.target.value })} placeholder="Total Wireless DC" data-testid="new-app-tw-dc" /></div>
                  <div><Label>Store Phone (optional)</Label><Input value={formData.store_phone} onChange={(e) => setFormData({ ...formData, store_phone: e.target.value })} placeholder="Leave blank for Coming soon" data-testid="new-app-store-phone" /></div>
                  <div><Label>Store Email (optional)</Label><Input type="email" value={formData.store_email} onChange={(e) => setFormData({ ...formData, store_email: e.target.value })} placeholder="Leave blank for Coming soon" data-testid="new-app-store-email" /></div>
                </div>
                <p className="text-[11px] text-slate-500">These become the Location name, TSP ID and TW DC after this applicant is converted to a dealer.</p>
              </div>
              <div><Label>Message</Label><Textarea value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} rows={3} /></div>
              <Button type="submit" disabled={submitting} className="w-full bg-teal hover:bg-teal-dark text-white" data-testid="submit-new-app-btn">
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</> : "Create Application"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Bulk Upload Dialog */}
        <Dialog open={isBulkOpen} onOpenChange={setIsBulkOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="font-heading text-navy">Bulk Upload Applications</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-600 font-body">Upload a CSV with columns: <code className="bg-slate-100 px-1 rounded text-xs">first_name, last_name, email, phone, company_name, city, state</code></p>
              <Button variant="outline" size="sm" onClick={() => downloadSampleCSV("applications")} className="w-full" data-testid="download-sample-apps-csv">
                <Download className="w-4 h-4 mr-2" />Download Sample CSV
              </Button>
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                <input type="file" accept=".csv" onChange={handleBulkUpload} className="hidden" id="bulk-app-upload" data-testid="bulk-app-file-input" />
                <label htmlFor="bulk-app-upload" className="cursor-pointer">
                  {submitting ? <Loader2 className="w-8 h-8 animate-spin text-teal mx-auto" /> : (
                    <><Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" /><p className="text-sm text-slate-600 font-body">Click to upload CSV</p></>
                  )}
                </label>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
